import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { AlertTriangle, CheckCircle2, ClipboardList, Clock3, Search, XCircle } from "lucide-react";
import { AdminLayout } from "../../components/AdminLayout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { AdminStatCard } from "../../components/admin/AdminStatCard";
import { adminHeaders, api } from "../../lib/api";
import { money, shortDate } from "../../lib/format";
import {
  copyRawJson,
  formatReviewRequestSummary,
  prettyRawJson,
  reviewStatusLabel,
  reviewTypeLabel
} from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";

interface ReviewRequestRow {
  id: number;
  request_no?: string | null;
  member_account: string;
  review_type: string;
  status: string;
  title?: string | null;
  amount?: number | null;
  reward_type?: string | null;
  source_activity_code?: string | null;
  payload_json?: string | null;
  applicant_note?: string | null;
  review_note?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at: string;
}

interface MemberDetail {
  member: {
    member_account: string;
    status: string;
    status_label: string;
  };
}

type ReviewAction = "approve" | "reject" | "cancel" | "convert";
type ReviewTab = "manual" | "system" | "exception" | "done" | "all" | "allAll";

interface PendingReviewAction {
  row: ReviewRequestRow;
  action: ReviewAction;
}

const reviewTypes = [
  ["", "全部"],
  ["birthday_bonus", "生日紅包"],
  ["rescue_bonus", "救援金 / 回穩補給金"],
  ["line_checkin", "LINE 群打卡"],
  ["telegram_checkin", "Telegram 打卡"],
  ["gift_helper_review", "贈禮小幫手審核"],
  ["manual_compensation", "人工補發"],
  ["manual_clawback", "人工扣回"]
] as const;

const statuses = [
  ["", "全部"],
  ["pending", "待確認"],
  ["approved", "已通過"],
  ["rejected", "已拒絕"],
  ["canceled", "已取消"],
  ["converted_to_reward", "已轉派發"]
] as const;

const testReviews = [
  { review_type: "birthday_bonus", title: "生日紅包 local/dev 審核", amount: 0, applicant_note: "local/dev birthday bonus review" },
  { review_type: "rescue_bonus", title: "救援金 local/dev 審核", amount: 8888, applicant_note: "local/dev rescue bonus review" },
  { review_type: "line_checkin", title: "LINE 群打卡 local/dev 審核", amount: 8888, applicant_note: "local/dev line checkin review" }
] as const;

const reviewTabs: Array<{ key: ReviewTab; label: string; helper: string }> = [
  { key: "manual", label: "人工待審", helper: "需要客服判斷或建立派發單" },
  { key: "system", label: "系統自動處理", helper: "自動建單紀錄請到派發中心 / 贈禮小幫手查詢" },
  { key: "exception", label: "異常 / 需人工處理", helper: "資料不完整、資格異常或需要補救" },
  { key: "done", label: "已完成", helper: "只查詢，不顯示主要操作" },
  { key: "all", label: "全部（目前條件）", helper: "目前查詢條件下的全部審核紀錄" },
  { key: "allAll", label: "全部所有", helper: "清除所有查詢條件後的完整資料" }
];

const manualDecisionReviewTypes = new Set([
  "birthday_bonus",
  "rescue_bonus",
  "line_checkin",
  "telegram_checkin",
  "gift_helper_review",
  "manual_compensation",
  "manual_clawback"
]);

export function AdminReviewsPage() {
  const isLocalDev = isLocalDevFrontend();
  const [rows, setRows] = useState<ReviewRequestRow[]>([]);
  const [memberStatusByAccount, setMemberStatusByAccount] = useState<Record<string, { status: string; label: string }>>({});
  const [memberAccount, setMemberAccount] = useState("");
  const [reviewType, setReviewType] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeTab, setActiveTab] = useState<ReviewTab>("manual");
  const [filterResetVersion, setFilterResetVersion] = useState(0);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState<PendingReviewAction | null>(null);
  const [updatingReviewId, setUpdatingReviewId] = useState<number | null>(null);

  async function loadWithFilters(filters?: {
    memberAccount?: string;
    reviewType?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    setError("");
    const params = new URLSearchParams();
    const nextMemberAccount = filters?.memberAccount ?? memberAccount;
    const nextReviewType = filters?.reviewType ?? reviewType;
    const nextStatus = filters?.status ?? status;
    const nextDateFrom = filters?.dateFrom ?? dateFrom;
    const nextDateTo = filters?.dateTo ?? dateTo;
    if (nextMemberAccount.trim()) params.set("member_account", nextMemberAccount.trim());
    if (nextReviewType) params.set("review_type", nextReviewType);
    if (nextStatus) params.set("status", nextStatus);
    if (nextDateFrom) params.set("date_from", nextDateFrom);
    if (nextDateTo) params.set("date_to", nextDateTo);
    const query = params.toString();
    const nextRows = await api<ReviewRequestRow[]>(`/api/admin/reviews${query ? `?${query}` : ""}`, { headers: adminHeaders() });
    setRows(nextRows);
    await loadMemberStatuses(nextRows);
  }

  async function load() {
    await loadWithFilters();
  }

  async function clearSearchOnly() {
    flushSync(() => {
      setMemberAccount("");
      setFilterResetVersion((version) => version + 1);
    });
    await loadWithFilters({ memberAccount: "" });
  }

  async function clearAllConditions(nextTab: ReviewTab = "manual") {
    flushSync(() => {
      setMemberAccount("");
      setReviewType("");
      setStatus("");
      setDateFrom("");
      setDateTo("");
      setActiveTab(nextTab);
      setMessage("");
      setFilterResetVersion((version) => version + 1);
    });
    await loadWithFilters({ memberAccount: "", reviewType: "", status: "", dateFrom: "", dateTo: "" });
  }

  async function loadMemberStatuses(nextRows: ReviewRequestRow[]) {
    const accounts = Array.from(new Set(nextRows.map((row) => row.member_account).filter(Boolean)));
    const pairs = await Promise.all(accounts.map(async (account) => {
      try {
        const detail = await api<MemberDetail>(`/api/admin/members/${encodeURIComponent(account)}`, { headers: adminHeaders() });
        return [account, { status: detail.member.status, label: detail.member.status_label }] as const;
      } catch {
        return [account, { status: "", label: "會員資料讀取失敗" }] as const;
      }
    }));
    setMemberStatusByAccount(Object.fromEntries(pairs));
  }

  async function createTestReview(input: (typeof testReviews)[number]) {
    setMessage("");
    setError("");
    try {
      const isRescue = input.review_type === "rescue_bonus";
      const isBirthday = input.review_type === "birthday_bonus";
      const isLine = input.review_type === "line_checkin";
      const year = new Date().getFullYear();
      const today = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Taipei",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      }).format(new Date());
      const payload = isRescue
        ? {
            apply_month: "2026-08",
            reason: "local/dev test",
            applicant_note: input.applicant_note,
            risk_note: "local/dev",
            suggested_amount: input.amount,
            created_from: "admin_reviews_local_test",
            special_approval: true,
            manual_deposit_checked: true,
            manual_bet_checked: false,
            manual_risk_checked: true
          }
        : isBirthday
          ? { birthday_month: 6, birthday_day: 18, year }
          : isLine
            ? {
                line_nickname: "test003 LINE 暱稱",
                checkin_date: today,
                proof_url: "https://example.com/local-line-checkin.png",
                proof_text: "local/dev LINE 群打卡證明",
                applicant_note: input.applicant_note,
                source: "line"
              }
            : null;
      await api<ReviewRequestRow>("/api/admin/reviews", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          member_account: "test003",
          review_type: input.review_type,
          title: input.title,
          amount: input.amount,
          reward_type: isRescue ? "rescue_fund_monthly" : isBirthday ? "birthday_bonus_manual" : isLine ? "group_checkin_bonus" : null,
          source_activity_code: isRescue ? "rescue_bonus" : isBirthday ? "birthday_bonus" : isLine ? "line_group_checkin" : null,
          payload_json: payload ? JSON.stringify(payload) : null,
          applicant_note: input.applicant_note,
          unique_key: isRescue ? "test003_rescue_fund_202608" : isBirthday ? `test003_birthday_bonus_${year}` : isLine ? `test003_line_checkin_${today}_local_test` : null,
          created_by: "local/dev"
        })
      });
      setMessage(`已建立 test003 ${reviewTypeLabel(input.review_type)} 審核資料。`);
      setActiveTab("manual");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "建立 local/dev 審核資料失敗。");
    }
  }

  async function updateReview(id: number, action: "approve" | "reject" | "cancel") {
    setMessage("");
    setError("");
    let body: Record<string, string | null> = {};
    if (action === "approve") body = { review_note: "" };
    if (action === "reject") body = { reject_reason: "資料不符合活動資格" };
    if (action === "cancel") body = { cancel_reason: "客服取消處理" };

    setUpdatingReviewId(id);
    try {
      await api<ReviewRequestRow>(`/api/admin/reviews/${id}/${action}`, {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify(body)
      });
      setMessage(reviewActionSuccessMessage(id, action));
      setPendingAction(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "審核操作失敗。");
      setPendingAction(null);
    } finally {
      setUpdatingReviewId(null);
    }
  }

  async function convertToReward(row: ReviewRequestRow) {
    setMessage("");
    setError("");
    let body: Record<string, string | number | null> = {};
    if (row.review_type === "birthday_bonus") {
      const amountText = window.prompt("請輸入生日紅包核定金額，最高 38,888", "");
      const amount = Number(String(amountText ?? "").replace(/,/g, "").trim());
      if (!Number.isFinite(amount) || amount <= 0) {
        setError("生日紅包金額必須大於 0。");
        setPendingAction(null);
        return;
      }
      if (amount > 38888) {
        setError("生日紅包金額不可超過 38,888。");
        setPendingAction(null);
        return;
      }
      body = {
        amount,
        note: window.prompt("建立派發單備註", "") ?? ""
      };
    } else if (row.review_type === "manual_compensation") {
      body = {
        note: window.prompt("人工補發建立派發單備註", "") ?? ""
      };
    }

    setUpdatingReviewId(row.id);
    try {
      await api(`/api/admin/reviews/${row.id}/convert-to-reward`, {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify(body)
      });
      setMessage(`審核 #${row.id} 已建立派發單。`);
      setPendingAction(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "建立派發單失敗。");
      setPendingAction(null);
    } finally {
      setUpdatingReviewId(null);
    }
  }

  function requestReviewAction(row: ReviewRequestRow, action: ReviewAction) {
    setError("");
    setMessage("");
    setPendingAction({ row, action });
  }

  async function confirmReviewAction() {
    if (!pendingAction) return;
    if (pendingAction.action === "convert") {
      await convertToReward(pendingAction.row);
      return;
    }
    await updateReview(pendingAction.row.id, pendingAction.action);
  }

  function reset() {
    clearAllConditions("manual").catch((err) => setError(err instanceof Error ? err.message : "審核資料讀取失敗。"));
  }

  useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : "審核資料讀取失敗。"));
  }, []);

  const classifiedRows = useMemo(() => classifyRows(rows, memberStatusByAccount), [rows, memberStatusByAccount]);
  const visibleRows = useMemo(() => {
    if (activeTab === "manual") return classifiedRows.manual;
    if (activeTab === "system") return classifiedRows.system;
    if (activeTab === "exception") return classifiedRows.exception;
    if (activeTab === "done") return classifiedRows.done;
    if (activeTab === "allAll") return rows;
    return rows;
  }, [activeTab, classifiedRows, rows]);

  const tabCounts: Record<ReviewTab, number> = {
    manual: classifiedRows.manual.length,
    system: classifiedRows.system.length,
    exception: classifiedRows.exception.length,
    done: classifiedRows.done.length,
    all: rows.length,
    allAll: rows.length
  };

  const stats = useMemo(() => ({
    manual: classifiedRows.manual.length,
    exception: classifiedRows.exception.length,
    done: classifiedRows.done.length,
    all: rows.length
  }), [classifiedRows, rows.length]);

  const activeTabMeta = reviewTabs.find((tab) => tab.key === activeTab) ?? reviewTabs[0];
  const hasActiveFilters = Boolean(memberAccount.trim() || reviewType || status || dateFrom || dateTo);
  const activeFilterText = [
    memberAccount.trim() ? `目前搜尋：${memberAccount.trim()}` : "",
    reviewType ? `審核類型：${reviewTypeLabel(reviewType)}` : "",
    status ? `狀態：${reviewStatusLabel(status)}` : "",
    dateFrom || dateTo ? `日期：${dateFrom || "不限"} ~ ${dateTo || "不限"}` : ""
  ].filter(Boolean).join("｜");

  return (
    <AdminLayout title="審核中心">
      {message ? <p className="result">{message}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}

      <section className="admin-dashboard-grid">
        <AdminStatCard label="人工待審" value={stats.manual} helper="需要客服判斷或建立派發單" icon={Clock3} tone="gold" />
        <AdminStatCard label="異常 / 需人工處理" value={stats.exception} helper="資料不完整或需要追蹤" icon={AlertTriangle} tone="red" />
        <AdminStatCard label="已完成" value={stats.done} helper="只保留查詢與明細" icon={CheckCircle2} tone="green" />
        <AdminStatCard label="全部紀錄" value={stats.all} helper="目前篩選條件下的筆數" icon={ClipboardList} tone="purple" />
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>審核中心</span>
          <h2>人工決策中心</h2>
        </div>
        <div className="summary-list">
          <p>預設只看「人工待審」，也就是客服需要判斷、通過、拒絕、取消或建立派發單的資料。</p>
          <p>系統自動建單與贈禮小幫手派發進度，請到派發中心或贈禮小幫手派發任務查詢，避免和人工待辦混在一起。</p>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>查詢</span>
          <h2>查詢條件</h2>
        </div>
        <AdminFilterBar key={`review-filter-${filterResetVersion}`}>
          <label>
            會員帳號
            <input value={memberAccount} onChange={(event) => setMemberAccount(event.target.value)} placeholder={isLocalDev ? "test003" : "輸入會員帳號"} autoComplete="off" />
          </label>
          <label>
            審核類型
            <select value={reviewType} onChange={(event) => setReviewType(event.target.value)}>
              {reviewTypes.map(([value, label]) => <option key={value || "all"} value={value}>{label}</option>)}
            </select>
          </label>
          <label>
            狀態
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {statuses.map(([value, label]) => <option key={value || "all"} value={value}>{label}</option>)}
            </select>
          </label>
          <label>
            開始日期
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label>
            結束日期
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <button className="admin-btn admin-btn-primary" onClick={load}><Search size={16} />查詢</button>
          <button className="admin-btn admin-btn-secondary" onClick={reset}>重設</button>
        </AdminFilterBar>
      </section>

      {isLocalDev ? <section className="admin-card">
        <div className="admin-card-head">
          <span>本機測試</span>
          <h2>local/dev 測試審核資料</h2>
        </div>
        <div className="cta-row">
          {testReviews.map((item) => (
            <button key={item.review_type} className="admin-btn admin-btn-secondary" onClick={() => createTestReview(item)}>
              建立 test003 {reviewTypeLabel(item.review_type)}
            </button>
          ))}
        </div>
      </section> : null}

      <section className="admin-card">
        <div className="admin-card-head">
          <span>分類清單</span>
          <h2>{activeTabMeta.label}</h2>
          <p>{activeTabMeta.helper}</p>
        </div>

        {hasActiveFilters ? (
          <div className="review-query-notice">
            <div>
              <strong>目前已套用查詢條件，分類筆數為篩選後結果。</strong>
              <p>{activeFilterText}</p>
            </div>
            <div className="cta-row compact">
              {memberAccount.trim() ? (
                <button className="admin-btn admin-btn-secondary" type="button" onClick={() => clearSearchOnly().catch((err) => setError(err instanceof Error ? err.message : "審核資料讀取失敗。"))}>
                  清除搜尋
                </button>
              ) : null}
              <button className="admin-btn admin-btn-secondary" type="button" onClick={() => clearAllConditions("manual").catch((err) => setError(err instanceof Error ? err.message : "審核資料讀取失敗。"))}>
                清除全部條件
              </button>
            </div>
          </div>
        ) : activeTab === "allAll" ? (
          <div className="review-query-notice">
            <strong>目前顯示完整資料，沒有套用查詢條件。</strong>
          </div>
        ) : null}

        <div className="tabs admin-status-tabs review-tabs" role="tablist" aria-label="審核中心分類">
          {reviewTabs.map((tab) => (
            <button
              key={tab.key}
              className={activeTab === tab.key ? "active" : ""}
              onClick={() => {
                if (tab.key === "allAll") {
                  clearAllConditions("allAll").catch((err) => setError(err instanceof Error ? err.message : "審核資料讀取失敗。"));
                  return;
                }
                setActiveTab(tab.key);
              }}
              type="button"
            >
              {tab.label}
              <strong>{tabCounts[tab.key]}</strong>
            </button>
          ))}
        </div>

        {activeTab === "system" ? <SystemAutoNotice /> : null}
        {activeTab === "exception" ? <ExceptionNotice /> : null}
        {activeTab === "done" ? <DoneNotice /> : null}
        {activeTab === "all" ? <AllNotice /> : null}

        <ReviewTable
          rows={visibleRows}
          activeTab={activeTab}
          memberStatusByAccount={memberStatusByAccount}
          onRequestAction={requestReviewAction}
          updatingReviewId={updatingReviewId}
        />
      </section>

      <ConfirmDialog
        open={Boolean(pendingAction)}
        title={pendingAction ? reviewConfirmCopy(pendingAction.action).title : ""}
        message={pendingAction ? reviewConfirmCopy(pendingAction.action).message : ""}
        confirmText={pendingAction ? reviewConfirmCopy(pendingAction.action).confirmText : "確認"}
        cancelText="取消"
        variant={pendingAction?.action === "reject" || pendingAction?.action === "cancel" ? "danger" : "normal"}
        loading={Boolean(pendingAction && updatingReviewId === pendingAction.row.id)}
        onCancel={() => {
          if (!updatingReviewId) setPendingAction(null);
        }}
        onConfirm={confirmReviewAction}
      />
    </AdminLayout>
  );
}

function SystemAutoNotice() {
  return (
    <div className="review-tab-notice">
      <strong>系統自動處理項目不在這裡人工審核。</strong>
      <p>常態回饋、紅包券抽中河幣、邀友獎勵、新人七關等自動建單項目，請到派發中心或贈禮小幫手派發任務查詢。</p>
      <div className="cta-row compact">
        <a className="admin-btn admin-btn-secondary" href="/admin/rewards">前往派發中心</a>
        <a className="admin-btn admin-btn-secondary" href="/admin/crawler">前往贈禮小幫手派發任務</a>
      </div>
    </div>
  );
}

function ExceptionNotice() {
  return (
    <div className="review-tab-notice warning">
      <strong>這裡只整理審核中心能判斷的異常。</strong>
      <p>派發失敗、無法排入贈禮小幫手、爬蟲回寫失敗等派發任務異常，請到贈禮小幫手派發任務查看原因。</p>
      <div className="cta-row compact">
        <a className="admin-btn admin-btn-secondary" href="/admin/crawler">查看派發任務異常</a>
      </div>
    </div>
  );
}

function DoneNotice() {
  return (
    <div className="review-tab-notice">
      <strong>已完成資料只供查詢。</strong>
      <p>已拒絕、已取消、已轉派發的資料不再顯示通過 / 拒絕 / 取消等主要操作。</p>
    </div>
  );
}

function AllNotice() {
  return (
    <div className="review-tab-notice">
      <strong>全部紀錄為查詢視角。</strong>
      <p>為避免誤操作，請切回「人工待審」頁籤處理審核與建立派發單。</p>
    </div>
  );
}

function reviewConfirmCopy(action: ReviewAction) {
  if (action === "approve") {
    return {
      title: "確認通過審核？",
      message: "通過後可能會建立派發單，請確認申請資料與金額正確。",
      confirmText: "確認通過"
    };
  }
  if (action === "reject") {
    return {
      title: "確認拒絕審核？",
      message: "拒絕後此申請會標記為已拒絕，請確認原因已填寫或資料無誤。",
      confirmText: "確認拒絕"
    };
  }
  if (action === "cancel") {
    return {
      title: "確認取消審核？",
      message: "取消後此申請將不再處理，請確認這是正確操作。",
      confirmText: "確認取消"
    };
  }
  return {
    title: "確認建立派發單？",
    message: "系統會依審核資料建立待派發獎勵，派發仍需到派發中心或贈禮小幫手流程確認完成。",
    confirmText: "確認建立"
  };
}

function reviewActionSuccessMessage(id: number, action: "approve" | "reject" | "cancel") {
  if (action === "approve") return `審核 #${id} 已通過。`;
  if (action === "reject") return `審核 #${id} 已拒絕。`;
  return `審核 #${id} 已取消。`;
}

function ReviewTable({
  rows,
  activeTab,
  memberStatusByAccount,
  onRequestAction,
  updatingReviewId
}: {
  rows: ReviewRequestRow[];
  activeTab: ReviewTab;
  memberStatusByAccount: Record<string, { status: string; label: string }>;
  onRequestAction: (row: ReviewRequestRow, action: ReviewAction) => void;
  updatingReviewId: number | null;
}) {
  const [copyMessage, setCopyMessage] = useState("");
  const allowActions = activeTab === "manual";

  async function copy(row: ReviewRequestRow) {
    await copyRawJson(row);
    setCopyMessage(`已複製審核 #${row.id} 的原始資料。`);
  }

  return (
    <>
      {copyMessage ? <p className="result">{copyMessage}</p> : null}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>單號</th>
              <th>會員帳號</th>
              <th>會員狀態</th>
              <th>審核類型</th>
              <th>金額</th>
              <th>狀態</th>
              <th>建立時間</th>
              <th>審核時間</th>
              <th>目前摘要 / 下一步</th>
              <th>操作</th>
              <th>明細</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const memberStatus = memberStatusByAccount[row.member_account];
              const isBirthday = row.review_type === "birthday_bonus";
              const eligible = !isBirthday || isBirthdayEligible(memberStatus?.status);
              const showIneligibleWarning = isBirthday && !eligible;
              return (
                <tr key={row.id}>
                  <td>{row.request_no ?? `#${row.id}`}</td>
                  <td><a className="admin-table-link" href={`/admin/members/${encodeURIComponent(row.member_account)}`}>{row.member_account}</a></td>
                  <td>{memberStatus?.label ?? "讀取中"}</td>
                  <td>{reviewTypeLabel(row.review_type)}</td>
                  <td>{row.amount ? money(row.amount) : "-"}</td>
                  <td>{reviewStatusLabel(row.status)}</td>
                  <td>{shortDate(row.created_at)}</td>
                  <td>{shortDate(row.reviewed_at)}</td>
                  <td>
                    <span>{formatReviewRequestSummary(row)}</span>
                    <p className="review-next-step">{nextStepText(row, activeTab, eligible)}</p>
                    {showIneligibleWarning ? (
                      <p className="review-warning">
                        會員目前狀態不符合生日紅包審核條件，請先確認會員身份或渠道狀態。
                      </p>
                    ) : null}
                  </td>
                  <td>{renderReviewActions(row, eligible, allowActions, onRequestAction, updatingReviewId)}</td>
                  <td>
                    <details className="raw-details">
                      <summary>原始資料</summary>
                      <pre>{prettyRawJson(row)}</pre>
                    </details>
                    <button className="admin-btn admin-btn-action" onClick={() => copy(row)}>複製原始資料</button>
                  </td>
                </tr>
              );
            })}
            {!rows.length ? <tr><td colSpan={11}>查無符合條件的審核資料。</td></tr> : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

function renderReviewActions(
  row: ReviewRequestRow,
  eligible: boolean,
  allowActions: boolean,
  onRequestAction: (row: ReviewRequestRow, action: ReviewAction) => void,
  updatingReviewId: number | null
) {
  const isUpdating = updatingReviewId === row.id;
  if (!allowActions) {
    return <span className="closed-status">僅供查詢</span>;
  }
  if (row.status === "pending") {
    return (
      <div className="table-actions">
        {eligible ? (
          <button className="admin-btn admin-btn-primary" onClick={() => onRequestAction(row, "approve")} disabled={isUpdating}>
            {isUpdating ? "處理中..." : "通過"}
          </button>
        ) : null}
        <button className="admin-btn admin-btn-danger" onClick={() => onRequestAction(row, "reject")} disabled={isUpdating}>
          {isUpdating ? "處理中..." : "拒絕"}
        </button>
        <button className="admin-btn admin-btn-danger" onClick={() => onRequestAction(row, "cancel")} disabled={isUpdating}>
          {isUpdating ? "處理中..." : "取消"}
        </button>
      </div>
    );
  }
  if (canConvertToReward(row)) {
    return (
      <div className="table-actions">
        <button className="admin-btn admin-btn-action" onClick={() => onRequestAction(row, "convert")} disabled={isUpdating}>
          {isUpdating ? "處理中..." : "建立派發單"}
        </button>
      </div>
    );
  }
  return <span className="closed-status">{closedStatusText(row)}</span>;
}

function classifyRows(rows: ReviewRequestRow[], memberStatusByAccount: Record<string, { status: string; label: string }>) {
  const exception = rows.filter((row) => isReviewException(row, memberStatusByAccount[row.member_account]));
  const manual = rows.filter((row) => isManualQueueRow(row) && !exception.some((item) => item.id === row.id));
  const system = rows.filter(isSystemAutoRecord);
  const done = rows.filter((row) => isDoneRow(row) && !system.some((item) => item.id === row.id));
  return { manual, system, exception, done };
}

function isManualQueueRow(row: ReviewRequestRow) {
  if (!manualDecisionReviewTypes.has(row.review_type)) return false;
  if (row.status === "pending") return true;
  if (row.status === "approved" && canConvertToReward(row)) return true;
  return false;
}

function isSystemAutoRecord(row: ReviewRequestRow) {
  const source = row.source_activity_code ?? "";
  return [
    "regular_deposit_rebate",
    "red_packet_draw",
    "invite_reward",
    "newbie_reward",
    "reward_crawler_dispatch"
  ].includes(source);
}

function isReviewException(row: ReviewRequestRow, memberStatus?: { status: string; label: string }) {
  if (!["pending", "approved", "rejected", "canceled", "converted_to_reward"].includes(row.status)) return true;
  if (row.status === "pending" && memberStatus && !memberStatus.status) return true;
  if (row.review_type === "birthday_bonus" && (row.status === "pending" || row.status === "approved") && !isBirthdayEligible(memberStatus?.status)) return true;
  return false;
}

function isDoneRow(row: ReviewRequestRow) {
  return ["rejected", "canceled", "converted_to_reward"].includes(row.status);
}

function canConvertToReward(row: ReviewRequestRow) {
  return ["birthday_bonus", "rescue_bonus", "manual_compensation"].includes(row.review_type) && row.status === "approved";
}

function nextStepText(row: ReviewRequestRow, activeTab: ReviewTab, eligible: boolean) {
  if (activeTab === "system") return "系統自動處理紀錄僅供查詢，派發進度請到派發中心或贈禮小幫手查看。";
  if (activeTab === "done") return "此筆已完成，僅保留明細查詢。";
  if (activeTab === "all") return "如需操作，請切到人工待審頁籤處理。";
  if (activeTab === "exception") return "請先確認異常原因，再依情況前往會員詳情、派發中心或贈禮小幫手處理。";
  if (!eligible) return "會員資格尚未符合，請先確認會員狀態。";
  if (row.status === "pending") return "下一步：請客服確認資料後通過、拒絕或取消。";
  if (canConvertToReward(row)) return "下一步：審核已通過，請建立派發單。";
  return "目前不需要主要操作。";
}

function closedStatusText(row: ReviewRequestRow) {
  if (row.status === "converted_to_reward") return "已轉派發";
  if (row.status === "rejected") return "已拒絕";
  if (row.status === "canceled") return "已取消";
  if (row.status === "approved") return "已通過";
  return "無可用操作";
}

function isBirthdayEligible(status?: string | null) {
  return ["channel_confirmed", "newbie_active", "graduated", "normal", "active"].includes(status ?? "");
}
